import {
  fontFamilies,
  FontSize,
  fontSizes,
  FontStyle,
  FontWeight,
  PinType,
  ShadowSettingKey,
  TextEditorHandler,
  TextHorizontalAlignmentKey,
  TextPinComponent,
  TextVerticalAlignmentKey,
  typographyType,
} from '@lessonup/pins-shared';

import { UpdatePinAction, UpdatePinDispatch } from '@lessonup/pin-renderer';
import { assertNever, capitalizeFirstCharacter } from '@lessonup/utils';
import { TFunction } from 'i18next';
import { useRef } from 'react';
import { SteppedNumberInputProps } from '../../components/EditorContextSidebar/components/CustomSelect/SteppedNumberInput';
export type FontWeightStyle = FontWeight | `${FontWeight}-${FontStyle}`;

const fontWeightStyles: FontWeightStyle[] = ['regular', 'regular-italic', 'bold', 'bold-italic'];

export const createFontShadowOptions = (t: TFunction): { label: string; value: ShadowSettingKey }[] => [
  { value: 'NONE', label: t('contextSidebar.settings.shadows.none') },
  { value: 'HARD', label: t('contextSidebar.settings.shadows.hard') },
  { value: 'MEDIUM', label: t('contextSidebar.settings.shadows.medium') },
  { value: 'SOFT', label: t('contextSidebar.settings.shadows.soft') },
];

export const createFontFamilyOptions = () => [
  ...fontFamilies.map((fontFamily) => ({ label: fontFamily, value: fontFamily })),
];

export const createTypographyOptions = () => [
  ...typographyType.map((typography) => ({ label: capitalizeFirstCharacter(typography), value: typography })),
];

export const createFontWeightStyleOptions = (
  t: TFunction
): {
  label: string;
  value: FontWeightStyle;
}[] => [
  ...fontWeightStyles.map((fontWeight) => ({
    label: t(`contextSidebar.text.fontWeightStyle.${fontWeight}`),
    value: fontWeight,
  })),
];

const fontSizeToInt = (fontSize: string) => parseInt(fontSize.replace('px', ''));
const intToFontSize = (fontSize: number) => `${fontSize}px`;

export const createFontSizeOptions = () =>
  fontSizes.map((fontSize) => ({ label: fontSize, value: fontSizeToInt(fontSize) }));

export function createHandleSetHorizontalTextAlignChange(
  alignment: TextHorizontalAlignmentKey,
  pinComponentId: string,
  dispatch: React.Dispatch<UpdatePinAction<PinType>>
) {
  return () => {
    dispatch({
      type: 'updatePinComponentSettings',
      pinComponentId,
      settings: { horizontalAlignment: alignment },
    });
  };
}

export function createHandleSetVerticalTextAlignChange(
  alignment: TextVerticalAlignmentKey,
  pinComponentId: string,
  dispatch: React.Dispatch<UpdatePinAction<PinType>>
) {
  return () => {
    dispatch({
      type: 'updatePinComponentSettings',
      pinComponentId,
      settings: { verticalAlignment: alignment },
    });
  };
}

export function onFontWeightStyleChange(textEditorHandler: TextEditorHandler, fontWeightStyle: FontWeightStyle) {
  switch (fontWeightStyle) {
    case 'bold': {
      textEditorHandler.italic.unset();
      textEditorHandler.bold.set();
      break;
    }
    case 'bold-italic': {
      textEditorHandler.bold.set();
      textEditorHandler.italic.set();
      break;
    }
    case 'regular-italic': {
      textEditorHandler.bold.unset();
      textEditorHandler.italic.set();
      break;
    }
    case 'regular': {
      textEditorHandler.bold.unset();
      textEditorHandler.italic.unset();
      break;
    }
    default: {
      assertNever(fontWeightStyle, 'Invalid fontWeightStyle');
    }
  }
}

export function getFontWeightStyleValue(textEditorHandler: TextEditorHandler): FontWeightStyle {
  if (textEditorHandler.bold.get() && textEditorHandler.italic.get()) {
    return 'bold-italic';
  }

  if (textEditorHandler.bold.get()) {
    return 'bold';
  }

  if (textEditorHandler.italic.get()) {
    return 'regular-italic';
  }

  return 'regular';
}

export function useFontSizePickerHelper(
  textEditorHandler: TextEditorHandler,
  pinComponent: TextPinComponent,
  dispatch: UpdatePinDispatch<PinType>,
  detailMode: boolean
): SteppedNumberInputProps {
  // This logic should probably be moved to a generic component wrapping the SteppedNumberInput
  const focusRef = useRef({ inputFocused: false, editorFocused: false });

  const inDetailMode = () => {
    if (focusRef.current.inputFocused) {
      return focusRef.current.editorFocused;
    }
    return detailMode;
  };

  const updateShouldTargetChild = () => textEditorHandler.updateShouldTargetChild(inDetailMode());

  const getFontSizeValue = () => {
    const fontSizeCursorValue = textEditorHandler.fontSize.get();
    return updateShouldTargetChild()
      ? fontSizeCursorValue
        ? fontSizeCursorValue
        : pinComponent.settings.fontSize
      : pinComponent.settings.fontSize;
  };

  const onFontSizeChange = (value: number) => {
    const fontSize = intToFontSize(value) as FontSize;
    if (updateShouldTargetChild()) {
      textEditorHandler.fontSize.set(fontSize);
    } else {
      dispatch({
        type: 'updatePinComponentSettings',
        pinComponentId: pinComponent.id,
        settings: {
          fontSize: fontSize,
        },
        debounce: 500,
      });
      textEditorHandler.fontSize.unsetAll();
    }
  };

  const value = getFontSizeValue();

  const fontSizePicker: SteppedNumberInputProps = {
    label: 'Font Size',
    value: value ? fontSizeToInt(value) : undefined,
    options: createFontSizeOptions(),
    valueValidation: (value) => value === '' || /^\d+$/.test(value),
    onTextChange: (value) => {
      onFontSizeChange(value);
    },
    onChange: onFontSizeChange,
    onInputFocus: () => {
      focusRef.current = { inputFocused: true, editorFocused: detailMode };
    },
    onBlur: () => {
      if (focusRef.current.editorFocused) {
        textEditorHandler.focus.keep();
      }
      focusRef.current = { inputFocused: false, editorFocused: false };
    },
  };
  return fontSizePicker;
}
